/* istanbul ignore file */
import React from 'react';
import type { SiteChromeContent } from '@noths/polaris-client-site-chrome-ui';
import type {
  GetServerSideProps,
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
} from 'next';

import type { InspirationCategoryProps } from 'src/components/templates/InspirationCategory/InspirationCategory';
import { InspirationCategory } from 'src/components/templates/InspirationCategory/InspirationCategory';
import { RibbonsPage } from 'src/components/templates/RibbonsPage/RibbonsPage';
import { SKIP_CONTENT_TARGET_ID } from 'src/constants/elementIds';
import { getPageContent } from 'src/content/getPageContent';
import { toInspirationCategoryProps } from 'src/content/inspiration/toInspirationCategoryProps';
import { toInspirationPostBreadcrumbs } from 'src/content/inspiration/toInspirationPostBreadcrumbs';
import { transformModularContentToUISections } from 'src/content/transformModularContentToUISections';
import { transformSeo } from 'src/content/transformSeo';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import {
  fetchInspirationLibrary,
  toInspirationTermRefs,
} from 'src/services/contentstack/fetchInspirationLibrary';
import {
  fetchInspirationTermNames,
  INSPIRATION_CATEGORY_TAXONOMY_UID,
} from 'src/services/contentstack/fetchInspirationTermNames';
import type { ContentPageResultProps, PageConfiguration } from 'src/types/PageProps';
import { getContentPageErrorResult } from 'src/utils/getContentPageErrorResult';
import { getPageMetadata } from 'src/utils/getPageMetadata';
import { parseInspirationPage } from 'src/utils/inspirationUrls';

const pageConfig: PageConfiguration = {
  env: process.env.NODE_ENV,
  contentType: 'inspiration',
  pageType: 'inspiration',
  trackingCategory: 'Inspiration',
};

type InspirationCategoryRouteProps = InspirationCategoryProps & {
  isInspirationCategory: true;
  pageEventCategory: 'Inspiration';
  siteChromeContent: SiteChromeContent | null;
};

type InspirationRouteProps = ContentPageResultProps | InspirationCategoryRouteProps;

/** Only a single segment, `/inspiration/<term uid>`, can be a category page. */
const getCandidateCategoryUid = ({ params }: GetServerSidePropsContext) => {
  const segments = params?.['inspiration-pages'];

  return Array.isArray(segments) && segments.length === 1 ? segments[0] : undefined;
};

// Categories and articles share `/inspiration/<segment>`, so a segment naming a
// category term renders that category's page and anything else is an article.
export const getServerSideProps: GetServerSideProps<InspirationRouteProps> = async (context) => {
  const pageMetadata = getPageMetadata(context);
  const { featureFlags, isPreview } = pageMetadata;
  const candidateCategoryUid = getCandidateCategoryUid(context);
  // Both routes need the names: to recognise a category, and to name an
  // article's category in its breadcrumbs. Never rejects; it falls back to {}.
  const categoryNamesRequest = fetchInspirationTermNames(
    INSPIRATION_CATEGORY_TAXONOMY_UID,
    isPreview,
  );

  if (candidateCategoryUid) {
    const categoryNames = await categoryNamesRequest;

    if (categoryNames[candidateCategoryUid]) {
      const page = parseInspirationPage(context.query.page);

      if (page === null) {
        return { notFound: true };
      }

      const [[siteChromeContent], library] = await Promise.all([
        fetchGlobalContent({ featureFlags, isPreview }),
        fetchInspirationLibrary(isPreview),
      ]);
      const categoryProps = toInspirationCategoryProps(
        library,
        categoryNames,
        candidateCategoryUid,
        page,
      );

      return categoryProps
        ? {
            props: {
              ...categoryProps,
              isInspirationCategory: true,
              pageEventCategory: 'Inspiration',
              siteChromeContent,
            },
          }
        : { notFound: true };
    }
  }

  try {
    const [{ pageContent, siteChromeContent }, categoryNames] = await Promise.all([
      getPageContent(pageConfig, pageMetadata),
      categoryNamesRequest,
    ]);

    return {
      props: {
        pageConfig,
        pageMetadata,
        pageContent: {
          breadcrumbs: toInspirationPostBreadcrumbs(
            pageContent.title,
            pageMetadata.pathname,
            toInspirationTermRefs(pageContent.taxonomies),
            categoryNames,
          ),
          sections: await transformModularContentToUISections(
            pageContent.modular_content,
            pageConfig,
            pageMetadata,
          ),
          title: pageContent.title,
          seo: transformSeo(pageContent.seo, pageConfig, pageMetadata.pathname),
        },
        pageEventCategory: pageConfig.trackingCategory,
        siteChromeContent,
      },
    };
  } catch (error) {
    return getContentPageErrorResult(context.res, error);
  }
};

const InspirationPage = (props: InferGetServerSidePropsType<typeof getServerSideProps>) =>
  'isInspirationCategory' in props ? (
    <InspirationCategory {...props} />
  ) : (
    <div id={SKIP_CONTENT_TARGET_ID}>
      <RibbonsPage {...props} />
    </div>
  );

export default InspirationPage;
